import React from 'react';
import PropTypes from 'prop-types';
import MenuItem from 'material-ui/MenuItem';
import { Box } from '@material-ui/core';
import moment from 'moment';
import { FormsySelect } from 'formsy-material-ui/lib';
import { FormsyDate, FormsyNHS } from '../common/Formsy';
import ApiSelector from '../../containers/common/selectors/selectorContainer';
import { textFieldStyle, datePickerStyle, common, dropDownStyle } from '../../theme/AllocateBaseTheme';
import {
  teamsEndpoint,
  cliniciansForUserEndpoint,
  activitiesEndpoint,
  referralServiceEndpoint,
} from '../../settings/constants';
import globals from '../../globals/communityGlobals';

const locale = globals.getArea('reports');

class FilterPanel extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isCliniciansFetchRequired: false,
      startDateRestriction: null,
      endDateRestriction: null,
      startDate: null,
      endDate: null,
      isReferralServicesFetchRequired: true,
      referralServiceStartDate: null,
      period: 'allDay',
      teamStatus: 'Active',
    };
    this.handleStartDateChange = this.handleStartDateChange.bind(this);
    this.handleEndDateChange = this.handleEndDateChange.bind(this);
  }

  shouldComponentUpdate = (nextProps, nextState) =>
    Object.values(nextProps.filters).join(',') !== Object.values(this.props.filters).join(',') ||
    this.state.teamIds !== nextState.teamIds ||
    this.state.teamStatus !== nextState.teamStatus ||
    this.state.clinicianIds !== nextState.clinicianIds ||
    this.state.activityId !== nextState.activityId ||
    this.state.startDate !== nextState.startDate ||
    this.state.endDate !== nextState.endDate ||
    this.state.referralServiceIds !== nextState.referralServiceIds ||
    this.state.referralServiceStartDate !== nextState.referralServiceStartDate;

  componentDidUpdate() {
    const {
      teamIds,
      startDate,
      endDate,
      nhsNumber,
      activityId,
      cliniciansIds,
      referralServiceIds,
      period,
      teamStatus,
    } = this.state;
    this.props.onValuesChange({
      period,
      nhsNumber,
      activityId,
      startDate: moment(startDate).format(globals.dateFormats.date),
      endDate: moment(endDate).format(globals.dateFormats.date),
      orgUnitsIds: teamIds,
      cliniciansIds,
      referralServiceIds,
      reportType: this.props.name,
      teamStatus,
    });
  }

  onValuesChange = (event, value) => {
    this.setState({ ...this.state, period: value });
    const {
      teamIds,
      startDate,
      endDate,
      nhsNumber,
      activityId,
      cliniciansIds,
      referralServiceIds,
      teamStatus,
    } = this.state;
    this.props.onValuesChange({
      period: value,
      nhsNumber,
      activityId,
      startDate: moment(startDate).format(globals.dateFormats.date),
      endDate: moment(endDate).format(globals.dateFormats.date),
      orgUnitsIds: teamIds,
      cliniciansIds,
      referralServiceIds,
      reportType: this.props.name,
      teamStatus,
    });
  };

  getDatesValidation = () => ({ validateDates: this.validateDates });

  getCliniciansIdsCallArgs = () => ({ teamIds: this.state.teamIds });

  getTeamStatusCallArgs = () => ({ teamStatus: this.state.teamStatus });

  getCliniciansIdsValue = () => this.state.clinicianIds || [];

  getTeamsIdsValue = () => (this.state.teamIds || []).map(id => ({ id }));

  getReferralServiceCallArgs = () => ({ startDate: this.state.referralServiceStartDate });

  getReferralServiceIdsValue = () => this.state.referralServiceIds || [];

  handleActivityChange = (event, key, value) => {
    this.setState({ activity: value });
  };

  handleTeamsChange = (event, key, value) => {
    this.setState({
      teamIds: value,
      isCliniciansFetchRequired: value && value.length > 0,
    });
  };

  handleTeamStatusChange = (event, value) => {
    this.setState({
      teamStatus: value,
    });
  };

  handleClinicianChange = (event, key, value) => {
    this.setState({ clinicianIds: value });
  };

  handleActivityChange = (event, key, value) => {
    this.setState({ activityId: value });
  };

  handleReferralServiceChange = (event, key, value) => {
    this.setState({ referralServiceIds: value, isReferralServicesFetchRequired: false });
  };

  validateDates = values => {
    if (!values.startDate || !values.endDate) {
      return true;
    }

    const startDate = moment(values.startDate, 'd');
    const endDate = moment(values.endDate, 'd');

    return startDate.isSameOrBefore(endDate) ? true : locale.getMsg('incorrectDates');
  };

  handleStartDateChange = (event, date) => {
    const { maxRangeInDays } = this.props.filtersRestrictions;
    if (!date) {
      // when handleDateClear is called on the date
      this.setState({ startDate: null, endDateRestriction: null });
    } else {
      if (maxRangeInDays) {
        const restriction = new Date(date);
        restriction.setDate(date.getDate() + maxRangeInDays);
        this.setState({ endDateRestriction: restriction, startDate: date });
      }
      this.setState({
        isReferralServicesFetchRequired: true,
        referralServiceStartDate: moment(date).format(globals.dateFormats.date),
        startDate: date,
      });
    }
  };

  handleEndDateChange = (event, date) => {
    const { maxRangeInDays } = this.props.filtersRestrictions;
    if (!date) {
      this.setState({ startDateRestriction: null, endDate: null });
    } else {
      // eslint-disable-next-line no-lonely-if
      if (maxRangeInDays) {
        const restriction = new Date(date);
        restriction.setDate(date.getDate() - maxRangeInDays);
        this.setState({
          startDateRestriction: restriction,
          endDate: date,
        });
      }
      this.setState({
        endDate: date,
      });
    }
  };

  render() {
    const { dateRange, period, teams, clinicians, nhsNumber, activity, referralServices } = this.props.filters;
    const { maxRangeInDays } = this.props.filtersRestrictions;
    const {
      activityId,
      startDateRestriction,
      endDateRestriction,
      startDate,
      endDate,
      teamIds,
      teamStatus,
    } = this.state;
    return (
      <div>
        {(dateRange || period) && (
          <div style={{ width: '100%' }}>
            <Box display="flex" flexDirection="row" p={1}>
              {dateRange && (
                <Box p={1} flexGrow={1} style={{ width: '33%' }}>
                  <FormsyDate
                    floatingLabelText={`${locale.getMsg('dateFrom')} *`}
                    className="date-picker"
                    name="startDate"
                    required
                    validations={this.getDatesValidation()}
                    floatingLabelStyle={common.floatingLabelStyle}
                    floatingLabelShrinkStyle={common.floatingLabelShrinkStyle}
                    floatingLabelFocusStyle={common.floatingLabelFocusStyle}
                    underlineStyle={common.underlineStyle}
                    textFieldStyle={datePickerStyle.textFieldStyle}
                    inputStyle={textFieldStyle.inputStyle}
                    maxDate={maxRangeInDays && endDate ? endDate : undefined}
                    minDate={startDateRestriction}
                    onChange={this.handleStartDateChange}
                  />
                </Box>
              )}
              {dateRange && (
                <Box p={1} flexGrow={1} style={{ width: '33%' }}>
                  <FormsyDate
                    className="date-picker"
                    floatingLabelText={`${locale.getMsg('dateTo')} *`}
                    name="endDate"
                    required
                    floatingLabelStyle={common.floatingLabelStyle}
                    floatingLabelShrinkStyle={common.floatingLabelShrinkStyle}
                    floatingLabelFocusStyle={common.floatingLabelFocusStyle}
                    underlineStyle={common.underlineStyle}
                    textFieldStyle={datePickerStyle.textFieldStyle}
                    inputStyle={textFieldStyle.inputStyle}
                    maxDate={maxRangeInDays && endDateRestriction ? endDateRestriction : endDate}
                    minDate={startDate || endDateRestriction}
                    onChange={this.handleEndDateChange}
                  />
                </Box>
              )}
              {period && (
                <Box p={1} flexGrow={1} style={{ width: '33%' }}>
                  <FormsySelect
                    value={this.state.period}
                    required
                    name="period"
                    floatingLabelText={`${locale.getMsg('period')} *`}
                    floatingLabelStyle={common.floatingLabelStyle}
                    floatingLabelShrinkStyle={common.floatingLabelShrinkStyle}
                    floatingLabelFocusStyle={common.floatingLabelFocusStyle}
                    underlineStyle={common.underlineStyle}
                    inputStyle={textFieldStyle.inputStyle}
                    style={dropDownStyle.style}
                    iconStyle={dropDownStyle.iconStyle}
                    labelStyle={dropDownStyle.labelStyle}
                    menuStyle={dropDownStyle.menuStyle}
                    onChange={(event, value) => this.onValuesChange(event, value)}
                  >
                    <MenuItem value="dayOnly" primaryText={locale.getMsg('dayOnly')} />
                    <MenuItem value="twilightOnly" primaryText={locale.getMsg('twilightOnly')} />
                    <MenuItem value="allDay" primaryText={locale.getMsg('allDay')} />
                  </FormsySelect>
                </Box>
              )}
            </Box>
          </div>
        )}
        {teams && (
          <Box display="flex" flexDirection="row" p={1}>
            <Box p={1} flexGrow={1} style={{ width: '33%' }}>
              <FormsySelect
                value={teamStatus}
                required
                name="teamStatus"
                floatingLabelText={'Team status'}
                floatingLabelStyle={common.floatingLabelStyle}
                floatingLabelShrinkStyle={common.floatingLabelShrinkStyle}
                floatingLabelFocusStyle={common.floatingLabelFocusStyle}
                underlineStyle={common.underlineStyle}
                inputStyle={textFieldStyle.inputStyle}
                style={dropDownStyle.style}
                iconStyle={dropDownStyle.iconStyle}
                labelStyle={dropDownStyle.labelStyle}
                menuStyle={dropDownStyle.menuStyle}
                onChange={this.handleTeamStatusChange}
              >
                <MenuItem value="Active" primaryText={'Active'} />
                <MenuItem value="Inactive" primaryText={'Inactive'} />
                <MenuItem value="All" primaryText={'All'} />
              </FormsySelect>
            </Box>
            <Box p={1} flexGrow={1} style={{ width: '33%' }}>
              <ApiSelector
                endpoint={teamsEndpoint}
                reduxIdentifier="teams"
                multiple
                required
                validations={{ minLength: 1 }}
                floatingLabelText={`${locale.getMsg('teams')}*`}
                name="teamsIds"
                callArgs={this.getTeamStatusCallArgs()}
                isFetchRequired
                value={this.getTeamsIdsValue()}
                onChange={this.handleTeamsChange}
              />
            </Box>
            {clinicians && (
              <Box p={1} flexGrow={1} style={{ width: '33%' }}>
                <ApiSelector
                  name="cliniciansIds"
                  endpoint={cliniciansForUserEndpoint}
                  reduxIdentifier="cliniciansIds"
                  floatingLabelText={locale.getMsg('clinicians')}
                  isFetchRequired={this.state.isCliniciansFetchRequired}
                  callArgs={this.getCliniciansIdsCallArgs()}
                  multiple
                  onChange={this.handleClinicianChange}
                  value={this.getCliniciansIdsValue()}
                  disabled={teamIds === undefined || teamIds.length === 0}
                />
              </Box>
            )}
            {nhsNumber && (
              <Box p={1} flexGrow={1} style={{ width: '33%' }}>
                <FormsyNHS
                  name="nhsNumber"
                  floatingLabelText={locale.getMsg('nhsNumber')}
                  className="formsytext-css col-c4"
                  floatingLabelStyle={textFieldStyle.floatingLabelStyle}
                  floatingLabelShrinkStyle={textFieldStyle.floatingLabelShrinkStyle}
                  floatingLabelFocusStyle={datePickerStyle.floatingLabelFocusStyle}
                  style={textFieldStyle.style}
                  inputStyle={textFieldStyle.inputStyle}
                  underlineStyle={textFieldStyle.underlineStyle}
                  underlineFocusStyle={textFieldStyle.underlineFocusStyle}
                  validationColor={textFieldStyle.validationColor}
                />
              </Box>
            )}
          </Box>
        )}
        {activity && (
          <Box display="flex" flexDirection="row" p={1}>
            <Box p={1} flexGrow={1}>
              <ApiSelector
                endpoint={activitiesEndpoint}
                reduxIdentifier="activityId"
                floatingLabelText={`${locale.getMsg('activities')} *`}
                name="activityId"
                multiple={false}
                required
                isFetchRequired
                onChange={this.handleActivityChange}
                value={activityId}
                phoneCallHelperText
              />
            </Box>
            <Box p={1} flexGrow={1} />
            <Box p={1} flexGrow={1} />
          </Box>
        )}
        {referralServices && (
          <Box display="flex" flexDirection="row" p={1}>
            <Box p={1} flexGrow={1}>
              <ApiSelector
                endpoint={referralServiceEndpoint}
                reduxIdentifier="referralServiceIds"
                floatingLabelText={locale.getMsg('referralServices')}
                name="referralServiceIds"
                multiple
                isFetchRequired={this.state.isReferralServicesFetchRequired}
                callArgs={this.getReferralServiceCallArgs()}
                onChange={this.handleReferralServiceChange}
                value={this.getReferralServiceIdsValue()}
              />
            </Box>
            <Box p={1} flexGrow={1} />
            <Box p={1} flexGrow={1} />
          </Box>
        )}
      </div>
    );
  }
}

FilterPanel.defaultProps = {
  filters: [],
  filtersRestrictions: {
    maxRangeInDays: null,
  },
};

FilterPanel.propTypes = {
  onValuesChange: PropTypes.func.isRequired,
  filters: PropTypes.shape({
    dateRange: PropTypes.bool,
    clinicians: PropTypes.bool,
    nhsNumber: PropTypes.bool,
    teams: PropTypes.bool,
    period: PropTypes.bool,
    activity: PropTypes.bool,
  }),
  filtersRestrictions: PropTypes.shape({
    maxRangeInDays: PropTypes.number,
  }),
};
export default FilterPanel;
